import type {
  CaseOutcome,
  RunDetail,
  TestResult,
} from '@/lib/api/quality-types.js';

import {
  caseOutcome,
  isOutcomeReassessmentCandidate,
  isUnconfirmedDefect,
  isUnconfirmedQaAssessment,
  outcomeProjection,
  runLifecycleStatus,
  type OutcomeProjection,
} from '../quality-outcome.js';
import type { AgentAction } from './agentActionHandoff.js';

/** A run action the next-step strip can offer; the ids match the run-actions busy kinds. */
export type NextStepId =
  AgentAction | 'analyze' | 'reassess_all' | 'pdf' | 'task';

/** What the strip needs to know about a run to pick its steps. */
export interface NextStepInput {
  /** Canonical business execution: the follow-up drafts and reassessment exist. */
  followUpsAvailable: boolean;
  /** The run actions offer a task draft (legacy or non-execution runs). */
  createTaskAvailable: boolean;
  defects: number;
  unclear: number;
  /** QA and infrastructure errors a QA error report would send (confirmed ones only). */
  qaErrors: number;
  /** Every QA and infrastructure error, unconfirmed assessments included. */
  qaOutcomes: number;
  reassessable: number;
}

/** Cases each follow-up draft would send, counted from the same selection the drafts use. */
export interface NextStepCounts {
  defects: number;
  unclear: number;
  qaErrors: number;
}

/** Everything the strip shows for a run. */
export interface NextStepModel {
  summary: string;
  steps: NextStepId[];
  counts: NextStepCounts;
}

const MAX_NEXT_STEPS = 3;

const ACCEPTED_OUTCOMES: Record<AgentAction, ReadonlySet<string>> = {
  file_defects: new Set(['defect_found']),
  rework_cases: new Set(['case_unclear']),
  report_qa_errors: new Set(['qa_error', 'infra_error']),
};

/** The cases a follow-up draft would send: matching outcomes, minus unconfirmed defects and QA assessments. */
export function selectActionCases(
  results: TestResult[],
  action: AgentAction,
): { caseId: string; outcome: CaseOutcome }[] {
  return results.flatMap((result) => {
    const outcome = caseOutcome(result);
    if (!outcome || !ACCEPTED_OUTCOMES[action].has(outcome)) return [];
    if (isUnconfirmedQaAssessment(result) || isUnconfirmedDefect(result))
      return [];
    return [{ caseId: result.case_id, outcome }];
  });
}

/** The ordered steps that fit the run's outcome, capped at three; the first one is primary. */
export function pickNextSteps(input: NextStepInput): NextStepId[] {
  const steps: NextStepId[] = [];
  if (input.followUpsAvailable) {
    if (input.defects > 0) steps.push('file_defects', 'analyze');
    if (input.unclear > 0) steps.push('rework_cases');
    if (input.qaErrors > 0) steps.push('report_qa_errors');
    if (input.qaOutcomes > 0 && input.reassessable > 0)
      steps.push('reassess_all');
  }
  if (steps.length === 0) {
    steps.push('pdf');
    if (input.createTaskAvailable) steps.push('task');
  }
  return steps.slice(0, MAX_NEXT_STEPS);
}

function plural(count: number, singular: string, pluralForm: string): string {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

/** The button label for a step. */
export function nextStepLabel(
  step: NextStepId,
  counts: NextStepCounts,
): string {
  switch (step) {
    case 'file_defects':
      return `File ${plural(counts.defects, 'defect', 'defects')}`;
    case 'rework_cases':
      return `Rework ${plural(counts.unclear, 'case', 'cases')}`;
    case 'report_qa_errors':
      return 'Report QA errors';
    case 'analyze':
      return 'Analyze in Agent';
    case 'reassess_all':
      return 'Reassess eligible cases';
    case 'pdf':
      return 'Download report';
    case 'task':
      return 'Create task';
  }
}

/** How the run ended, from its lifecycle status (canonical execution status or legacy status). */
function summaryOpening(lifecycle: string | null): string {
  switch (lifecycle) {
    case 'cancelled':
      return 'Run was cancelled.';
    case 'partially_completed':
    case 'not_completed':
    case 'incomplete':
      return 'Run did not complete.';
    default:
      return 'Run finished.';
  }
}

/** One sentence on how the run ended, from the canonical outcome counters when they exist. */
export function runOutcomeSummary(
  projection: OutcomeProjection,
  businessExecution: boolean,
  lifecycle: string | null,
): string {
  const opening = summaryOpening(lifecycle);
  if (projection.kind !== 'canonical' || !businessExecution) return opening;
  const { results, total } = projection;
  const qaErrors = projection.confirmedQaErrors + results.infra_error;
  const findings = [
    results.defect_found > 0
      ? `${plural(results.defect_found, 'defect', 'defects')} found`
      : null,
    results.case_unclear > 0
      ? plural(results.case_unclear, 'case is unclear', 'cases are unclear')
      : null,
    qaErrors > 0 ? plural(qaErrors, 'QA error', 'QA errors') : null,
    projection.unconfirmedQaErrors > 0
      ? plural(
          projection.unconfirmedQaErrors,
          'unconfirmed assessment',
          'unconfirmed assessments',
        )
      : null,
    results.unresolved
      ? plural(
          results.unresolved,
          'case requires review',
          'cases require review',
        )
      : null,
    results.assessing
      ? plural(
          results.assessing,
          'case is being assessed',
          'cases are being assessed',
        )
      : null,
  ].filter((finding): finding is string => finding !== null);
  if (findings.length > 0) return `${opening} ${findings.join(', ')}.`;
  if (total > 0 && results.passed === total)
    return total === 1
      ? `${opening} The case passed.`
      : `${opening} All ${total} cases passed.`;
  if (results.passed > 0)
    return `${opening} ${results.passed} of ${total} cases passed.`;
  return opening;
}

/** The strip's summary, steps and counts, gated exactly like the run actions panel. */
export function nextStepModel(detail: RunDetail): NextStepModel {
  const projection = outcomeProjection(
    detail.run,
    detail.summary,
    detail.results,
  );
  const businessExecution =
    (detail.run.run_kind ?? 'execution') === 'execution';
  const followUpsAvailable =
    projection.kind === 'canonical' && businessExecution;
  const countOf = (action: AgentAction): number =>
    followUpsAvailable ? selectActionCases(detail.results, action).length : 0;
  const counts: NextStepCounts = {
    defects: countOf('file_defects'),
    unclear: countOf('rework_cases'),
    qaErrors: countOf('report_qa_errors'),
  };
  return {
    summary: runOutcomeSummary(
      projection,
      businessExecution,
      runLifecycleStatus(detail.run),
    ),
    steps: pickNextSteps({
      followUpsAvailable,
      createTaskAvailable:
        projection.kind !== 'unsupported' &&
        (projection.kind === 'legacy' || !businessExecution),
      ...counts,
      qaOutcomes: detail.results.filter((result) => {
        const outcome = caseOutcome(result);
        return outcome === 'qa_error' || outcome === 'infra_error';
      }).length,
      reassessable: detail.results.filter(isOutcomeReassessmentCandidate)
        .length,
    }),
    counts,
  };
}
